/**
 * Facts of «Voice of the customer» over time (#847, docs/plans/2026-10-02-voc-trends.md §2).
 *
 * The closed sets of the Postgres tables `voc_analysis_facts` and
 * `voc_signal_keys` (migration 202610162034_voc_trend_facts.sql). Each list is
 * also the table's CHECK, so a value added here without a migration is
 * rejected by the database.
 *
 * - source: who wrote the row — the analysis itself (`analysis`) or the later
 *   voice step #843 (`voice_extraction`); each carries its own blocks.
 * - signal: the block a key belongs to; `voc_analysis_facts.blocks` lists the
 *   signals a source carried, which is the denominator N of that block.
 */
import { z } from 'zod/v4';

export const VOC_FACT_SOURCES = ['analysis', 'voice_extraction'] as const;
export const vocFactSourceSchema = z.enum(VOC_FACT_SOURCES);
export type VocFactSource = z.infer<typeof vocFactSourceSchema>;

/**
 * The signals each source carries — the CHECK of both tables: the analysis
 * (`result_json`) carries objections, competitors, product feedback and market
 * signals; the voice step (`voice_extraction_results`) carries question
 * subjects, decline reasons and themes.
 */
export const VOC_FACT_SIGNALS_BY_SOURCE = {
  analysis: ['objection', 'competitor', 'product_feedback', 'market_signal'],
  voice_extraction: ['question_subject', 'decline_reason', 'theme'],
} as const satisfies Record<VocFactSource, readonly string[]>;

export const VOC_FACT_SIGNALS = [
  ...VOC_FACT_SIGNALS_BY_SOURCE.analysis,
  ...VOC_FACT_SIGNALS_BY_SOURCE.voice_extraction,
] as const;
export const vocFactSignalSchema = z.enum(VOC_FACT_SIGNALS);
export type VocFactSignal = z.infer<typeof vocFactSignalSchema>;

/**
 * Ceiling of `raw_key` and `resolved_key`, in Unicode code points — Postgres
 * `char_length`, not JS `length` (UTF-16 units). Both columns sit in B-tree
 * indexes, whose row is capped at 2704 bytes: 200 code points are at most 800
 * bytes in UTF-8, so the whole key row stays under the cap. The writer cuts a
 * longer normalized key by code points before the insert —
 * `Array.from(key).slice(0, VOC_SIGNAL_KEY_MAX_LENGTH).join('')`; `key.slice`
 * could split a surrogate pair — otherwise the fact and all its keys roll back.
 */
export const VOC_SIGNAL_KEY_MAX_LENGTH = 200;

/**
 * The key cut to `VOC_SIGNAL_KEY_MAX_LENGTH` code points — how the writer
 * stores it and how a reader asks for it: one rule for both sides, or a long
 * key misses its series.
 */
export function cutVocSignalKey(key: string): string {
  const points = Array.from(key);
  return points.length <= VOC_SIGNAL_KEY_MAX_LENGTH
    ? key
    : points.slice(0, VOC_SIGNAL_KEY_MAX_LENGTH).join('');
}
